/**
 * Public route and landing content matrix (R-EXP-01, R-SEO-01, R-SEO-02,
 * R-BRAND-02, R-VISUAL-05, R-COMP-01, R-COMMERCE-01).
 *
 * Copy is concrete, sensory, and restrained. Bakhoor appears only as
 * atmosphere; the giveaway is described as country-allowlisted, never open
 * worldwide; the drop is standalone and monthly, never a subscription. The
 * `assertApprovedCopy` / `scanBannedCopy` guards enforce that posture, and the
 * contract test runs every string in `ALL_ROUTE_COPY` through them.
 */

import { COLLECTION_COPY, COLLECTION_PRICE_USD, COMMERCE_CURRENCY, EDITION_PRICE_USD } from '@mist/content/collection'

/**
 * The retired seven-beat Eclipse/Continuum landing sequence, verbatim. It
 * feeds only the retired EclipseHero, ScentContinuum, DuneStory, and
 * VesselRecord components and is deliberately excluded from
 * `ALL_ROUTE_COPY` — those components are archived as-is pending removal,
 * not part of the approved copy surface.
 */
export const RETIRED_LANDING_SEQUENCE = [
  {
    id: 'eclipse-hero',
    title: 'MIST by BDQ',
    body: [
      'Edition 001 — Peridot. Launching August 8, 2026.',
      'Get the launch email.',
      'Email me when MIST by BDQ Edition 001 opens.',
    ],
  },
  {
    id: 'dune-horizon',
    title: 'Dune Horizon',
    body: [
      'Citrus and ginger open bright over a warm-weather horizon; vetiver grounds the trail in root, smoke, and earth; musk holds close to the skin.',
      'Bakhoor lives in the imagery and atmosphere only.',
      'Clear heavy crystal, an olive-gold liquid, a faceted peridot cap, and a gold collar.',
    ],
  },
  {
    id: 'product-reveal',
    title: 'The Reveal',
    body: [
      'The sliding sleeve draws back, the tray lifts, and the bottle rises.',
      'A static, reduced-motion, and no-WebGL path tells the same story.',
    ],
  },
  {
    id: 'vessel-composition',
    title: 'Vessel',
    body: [
      '11% fragrance-oil concentration in 100 mL: citrus 40%, ginger 25%, vetiver 20%, musk 15%.',
      'Bottle about 68 x 38 x 142 mm; a supplied sliding sleeve about 90 x 60 x 165 mm over a black tray. No hinged box.',
    ],
  },
  {
    id: 'peridot-payoff',
    title: 'Confirm Your Place',
    body: [
      'We sent a verification email in a quiet peridot-refraction state.',
      'Your place on the launch list activates only after you confirm it.',
    ],
  },
  {
    id: 'archive',
    title: 'The Archive',
    body: [
      'Essays on peridot, vetiver, citrus and ginger, the crystal flacon, 11% concentration, and fragrance beyond gender.',
    ],
  },
  {
    id: 'footer-commerce',
    title: 'MIST by BDQ',
    body: [
      'MIST by BDQ is sold and fulfilled by PRNTD.',
      'A standalone, limited 100 mL drop, released monthly. Price is revealed at launch on August 8, 2026.',
    ],
  },
] as const

/** The approved landing sequence, in order (R-EXP-01). */
export const LANDING_SEQUENCE = [
  {
    id: 'hero',
    title: 'MIST by BDQ',
    body: [
      'Beauty. Divinity. Quality.',
      'Wear your birthstone, wear your story.',
      'Find your birthstone',
    ],
  },
  {
    id: 'collection',
    title: 'The Birthstone Collection',
    body: [
      'Twelve months, twelve stones — each carried in Pour Femme and Pour Homme expressions.',
      'Choose your month, read its verse, and set the bottle engraving and box signature before you add it to your bag.',
    ],
  },
  {
    id: 'editions',
    title: 'Special Occasions & Luxury Editions',
    body: [
      'Wedding, anniversary, couple, and collector presentations built around the birthstone pairings.',
    ],
  },
  {
    id: 'archive',
    title: 'The Archive',
    body: [
      'Essays on peridot, vetiver, citrus and ginger, the crystal flacon, 11% concentration, and fragrance beyond gender.',
    ],
  },
  {
    id: 'payoff',
    title: 'Confirm Your Place',
    body: [
      'Get the launch email.',
      'Email me when MIST by BDQ Edition 001 opens.',
      'Your place on the launch list activates only after you confirm it.',
    ],
  },
] as const

/**
 * The six launch Archive articles with canonical slugs (R-SEO-02). Each carries
 * an approved `dek` and `body`; copy is concrete and restrained, states no
 * price, availability, or prize, treats bakhoor as atmosphere only, and never
 * frames the drop as a subscription or the giveaway as worldwide (R-VISUAL-05,
 * R-SEO-02). Every string flows through the banned-copy guard via ALL_ROUTE_COPY.
 */
export const ARCHIVE_ARTICLES = [
  {
    title: 'Why Peridot Opens the MIST Calendar',
    slug: 'why-peridot-opens-the-mist-calendar',
    dek: 'August belongs to peridot: a green stone of light, chosen to open the monthly calendar.',
    body: [
      'Peridot is a stone of high summer — olivine formed deep and carried up, kept green by iron. It reads as light rather than shadow, which is why Edition 001 opens the calendar with it.',
      'The colour sets the direction for the whole edition: an olive-gold liquid under a faceted peridot cap, a warm-weather scent that stays bright before it settles.',
    ],
  },
  {
    title: 'Vetiver: Root, Smoke, and Earth',
    slug: 'vetiver-root-smoke-and-earth',
    dek: 'Vetiver is the root that grounds the trail — smoke, soil, and a cool green edge.',
    body: [
      'Vetiver is a grass, but the scent lives in its roots: dry, smoky, faintly bitter, with an earthiness that anchors lighter materials above it.',
      'In Edition 001 vetiver is twenty percent of the oil. It carries the citrus and ginger down into something that lingers close to the skin rather than flashing and fading.',
    ],
  },
  {
    title: 'Citrus and Ginger in Warm-Weather Fragrance',
    slug: 'citrus-and-ginger-in-warm-weather-fragrance',
    dek: 'The bright open of Edition 001: citrus lift and ginger heat over a warm horizon.',
    body: [
      'Citrus reads instantly as light and air; ginger adds a dry warmth that keeps it from turning simply sweet. Together they open Edition 001 at forty and twenty-five percent of the oil.',
      'Warm weather thins a fragrance, so the pairing is built to hold: the lift arrives first, the ginger keeps it moving, and the base beneath it does the staying.',
    ],
  },
  {
    title: 'The Crystal Flacon: Why Clear Glass Matters',
    slug: 'the-crystal-flacon-why-clear-glass-matters',
    dek: 'Clear heavy crystal shows the liquid honestly — colour, clarity, and light.',
    body: [
      'A clear, heavy crystal bottle hides nothing. The olive-gold liquid is part of the object, read directly through the glass rather than through a printed label.',
      'Weight matters too: a dense flacon sits differently in the hand, and the faceted peridot cap and gold collar catch light against the near-clear body.',
    ],
  },
  {
    title: 'What 11% Fragrance Oil Concentration Means',
    slug: 'what-11-fragrance-oil-concentration-means',
    dek: 'Concentration is a ratio, not a ranking — here is what 11% in 100 mL means.',
    body: [
      'Fragrance-oil concentration is the share of aromatic oils in the whole. Edition 001 is eleven percent in a 100 mL bottle, a considered middle that reads clearly without turning heavy.',
      'A higher number is not automatically better; it changes projection and character. Eleven percent suits a warm-weather composition meant to stay legible through the day.',
    ],
  },
  {
    title: 'Choosing Fragrance Beyond Gender',
    slug: 'choosing-fragrance-beyond-gender',
    dek: 'Scent has no gender — only materials, memory, and how a composition sits on you.',
    body: [
      'Materials do not carry a gender; conventions do. A fragrance is citrus, ginger, vetiver, and musk in proportion, and how those read is a matter of skin and memory, not category.',
      'Edition 001 is made to be chosen by anyone who likes how it wears — an object of art and ritual, not a label to live up to.',
    ],
  },
] as const

/** Route-safe approved Archive metadata. Human review PRs may move entries
 * here only after MDX status is approved and editorial/product/counsel-gate
 * fields are all true. Empty means drafts remain unpublished. */
export const APPROVED_ARCHIVE_ARTICLES: readonly typeof ARCHIVE_ARTICLES[number][] = [] as const


/**
 * House pages reached from the primary navigation (R-BRAND-02, R-VISUAL-05).
 * About restates the brand hierarchy and the collection's shape in approved
 * copy; Policies carries the seller, personalisation, and payment disclosures.
 */
export const ABOUT_PAGE = {
  title: 'About BDQ',
  kicker: 'The House of Birthstone Fragrances',
  body: [
    'BDQ crafts luxury, personalized birthstone scents mapped to individual profiles and life milestones, pairing artisanal perfumery with signature packaging.',
    'Twelve months, twelve stones — each carried in Pour Femme and Pour Homme expressions, with a custom bottle engraving and a box signature set into the side panel.',
    'MIST by BDQ is the primary brand, endorsed by BDQ. MIST by BDQ is sold and fulfilled by PRNTD.',
  ],
} as const

export const POLICIES_PAGE = {
  title: 'Our Policies',
  kicker: 'The Colophon',
  sections: [
    {
      heading: '01. Fulfillment & Seller Disclosure',
      body: 'MIST by BDQ is endorsed by BDQ and sold, fulfilled, and distributed exclusively by PRNTD.',
    },
    {
      heading: '02. Custom Engraving & Signatures',
      body: 'Custom bottle engravings and box signatures (e.g., "Boorenie") are personalized upon order confirmation.',
    },
    {
      heading: '03. Payments',
      body: 'Payments are processed securely by Stripe at checkout.',
    },
  ],
} as const

/** Indexable public routes (R-SEO-01, plus the About and Policies house pages). */
export const PUBLIC_ROUTES = [
  '/',
  '/about',
  '/edition-001/peridot',
  '/archive',
  '/archive/[slug]',
  '/giveaway',
  '/official-rules/[version]',
  '/policies',
  '/privacy',
  '/terms',
  '/accessibility',
  '/contact',
] as const

/** Routes that must send noindex and never appear in the sitemap (R-SEO-01). */
export const NOINDEX_ROUTES = ['/verify'] as const

/** Brand hierarchy and commerce disclosure (R-BRAND-02, R-COMMERCE-01, R-COMMERCE-02, R-COMP-01). */
export const COMMERCE_DISCLOSURE = {
  primaryBrand: 'MIST by BDQ',
  endorsement: 'BDQ',
  merchant: 'PRNTD',
  merchantRole: 'seller, merchant, and fulfiller',
  priceRevealAt: '2026-08-08T00:00:00-04:00',
  priceHiddenBeforeLaunch: true,
  disclosure: 'MIST by BDQ is sold and fulfilled by PRNTD.',
  giveawayPosture: 'Giveaway entry is country-allowlisted. No purchase necessary.',
  /**
   * The published prices and checkout processor (R-COMMERCE-02), derived from
   * the content constants and rendered by both the footer and /terms so the two
   * surfaces can never disagree.
   */
  pricing: `Each 100 mL bottle is $${COLLECTION_PRICE_USD} and each special edition is $${EDITION_PRICE_USD}, in ${COMMERCE_CURRENCY}. Checkout is processed by Stripe; PRNTD is the seller and fulfiller.`,
  links: 'Privacy, Terms, Accessibility, Contact, and the current Official Rules.',
} as const

/** The copy categories the guard rejects. */
export const BANNED_COPY_CATEGORIES = [
  'open-worldwide',
  'subscription',
  'fake-luxury',
  'bakhoor-as-ingredient',
] as const

export type BannedCopyCategory = (typeof BANNED_COPY_CATEGORIES)[number]

/** Every user-facing string in the matrix, for guard coverage. */
export const ALL_ROUTE_COPY: readonly string[] = [
  ...LANDING_SEQUENCE.flatMap((section) => [section.title, ...section.body]),
  ...ARCHIVE_ARTICLES.flatMap((article) => [article.title, article.dek, ...article.body]),
  ...COLLECTION_COPY,
  ABOUT_PAGE.title,
  ABOUT_PAGE.kicker,
  ...ABOUT_PAGE.body,
  POLICIES_PAGE.title,
  ...POLICIES_PAGE.sections.flatMap((section) => [section.heading, section.body]),
  COMMERCE_DISCLOSURE.disclosure,
  COMMERCE_DISCLOSURE.giveawayPosture,
  COMMERCE_DISCLOSURE.pricing,
  COMMERCE_DISCLOSURE.links,
]

/**
 * Returns the banned-copy categories a string trips, or `[]` when it is clean.
 * Distinguishes approved facts from banned framing: "released monthly" and
 * "monthly drop" are fine while "monthly subscription" is not; "Bakhoor ...
 * atmosphere" is fine while listing it as a note is not; a country-allowlisted
 * giveaway is fine while "open worldwide" is not.
 */
export function scanBannedCopy(text: string): BannedCopyCategory[] {
  const t = text.toLowerCase()
  const found: BannedCopyCategory[] = []

  const worldwide = /\bworldwide\b/.test(t)
  if (
    /\bopen\s+worldwide\b/.test(t) ||
    /\bworldwide\s+(giveaway|sweepstakes|draw|contest)\b/.test(t) ||
    (worldwide && /\b(giveaway|sweepstakes|enter|entry|win)\b/.test(t))
  ) {
    found.push('open-worldwide')
  }

  if (
    /\bsubscri(be|ption|bers?)\b/.test(t) ||
    /\bauto[- ]?ship\b/.test(t) ||
    /\brecurring\b/.test(t) ||
    /\bmonthly\s+(plan|subscription|box)\b/.test(t)
  ) {
    found.push('subscription')
  }

  if (
    /\bworld'?s\s+(best|finest|greatest)\b/.test(t) ||
    /(^|[^\d#])#1\b/.test(t) ||
    /\bnumber\s+one\b/.test(t) ||
    /\bclinically\s+proven\b/.test(t) ||
    /\bcenturies[- ]old\b/.test(t) ||
    /\bancient\s+(recipe|secret|formula)\b/.test(t) ||
    /\bguaranteed\b/.test(t) ||
    /\bunrival(?:l)?ed\b/.test(t) ||
    /\bbest[- ]?selling\b/.test(t)
  ) {
    found.push('fake-luxury')
  }

  if (/\bbakhoor\b/.test(t) && /\b(notes?|ingredients?|accord|contains)\b/.test(t)) {
    found.push('bakhoor-as-ingredient')
  }

  return found
}

/** Thrown when copy trips one or more banned-copy categories. */
export class MistCopyError extends Error {
  readonly categories: readonly BannedCopyCategory[]

  constructor(categories: readonly BannedCopyCategory[]) {
    super(`Copy trips banned categories: ${categories.join(', ')}`)
    this.name = 'MistCopyError'
    this.categories = categories
  }
}

/** Throws {@link MistCopyError} if `text` trips any banned-copy category. */
export function assertApprovedCopy(text: string): void {
  const categories = scanBannedCopy(text)
  if (categories.length > 0) {
    throw new MistCopyError(categories)
  }
}
